import { inject, Injectable } from "@angular/core";
import {
  AddConfigToProductListModalComponent,
  AddConfigToProductListRow,
} from "@components/add-config-to-product-list-modal/add-config-to-product-list-modal.component";
import { Device } from "@domain/product/monitoring/device";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { MonitoringDirectBomEntry, MonitoringDirectBomLine } from "@domain/project/direct-bom/monitoring-direct-bom-entry";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ModalService } from "@odx/angular/components/modal";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { MonitoringConfigurationFactory } from "@project/monitoring/monitoring-configuration.factory";
import { MonitoringSearchResult } from "@project/monitoring/monitoring-search-result";
import { ToastHelper } from "@utils/toast-helper.service";
import { Subject } from "rxjs";

@Injectable({ providedIn: "root" })
export class MonitoringProductListAddService {
  private modalService = inject(ModalService);
  private toastHelper = inject(ToastHelper);

  readonly productAdded$ = new Subject<void>();

  addToProductList(result: MonitoringSearchResult, project: MonitoringProject, deviceLookup: Map<string, Device>): void {
    if (result.kind === "group") {
      this.addGroupToProductList(result, project, deviceLookup);
    } else {
      this.addDeviceToProductList(result, project);
    }
  }

  private addDeviceToProductList(result: MonitoringSearchResult & { kind: "device" }, project: MonitoringProject): void {
    if (result.canAddToFloorplan === false) {
      this.addDirectDeviceToProductList(result, project);
      return;
    }

    const name = result.device.name;
    const existingConfig = project.findProductConfigByDevice(result.device.matNr);

    if (existingConfig) {
      this.showQuantityIncrementModal(existingConfig, name);
      return;
    }

    const config = MonitoringConfigurationFactory.fromSearchResult(
      result.category,
      result.family,
      result.option,
      name,
      project,
      result.slotRole,
    );
    if (!config) return;

    const rows: AddConfigToProductListRow[] = [{ quantity: 1, name: result.device.name, partNumber: result.device.matNr }];
    this.modalService
      .open(AddConfigToProductListModalComponent, {
        id: "add-config-to-product-list-modal",
        data: { title: name, rows, quantity: 1 },
      })
      .onClose$.subscribe({
        next: (quantity) => {
          if (typeof quantity !== "number") return;
          try {
            project.addProductConfig(config, result.category);
            config.setAdditionalProductListQuantity(quantity);
            this.showAddedToast(name);
            this.productAdded$.next();
          } catch {
            this.toastHelper.error(
              $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
              $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
            );
          }
        },
        error: () => {
          this.toastHelper.error(
            $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
            $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
          );
        },
      });
  }

  private addGroupToProductList(
    result: MonitoringSearchResult & { kind: "group" },
    project: MonitoringProject,
    deviceLookup: Map<string, Device>,
  ): void {
    if (result.canAddToFloorplan === false) {
      this.addDirectGroupToProductList(result, project, deviceLookup);
      return;
    }

    const groupName = result.option.groupName;
    const existingConfig = project.findProductConfigByGroup(groupName, result.option.devices);

    if (existingConfig) {
      this.showQuantityIncrementModal(existingConfig, groupName);
      return;
    }

    const config = MonitoringConfigurationFactory.fromSearchResult(
      result.category,
      result.family,
      result.option,
      groupName,
      project,
      "head",
    );
    if (!config) return;

    const rows: AddConfigToProductListRow[] = result.option.devices.map((matNr) => {
      const device = deviceLookup.get(matNr);
      return { quantity: 1, name: device?.name ?? matNr, partNumber: matNr };
    });

    this.modalService
      .open(AddConfigToProductListModalComponent, {
        id: "add-config-to-product-list-modal",
        data: { title: groupName, rows, quantity: 1 },
      })
      .onClose$.subscribe({
        next: (quantity) => {
          if (typeof quantity !== "number") return;
          try {
            project.addProductConfig(config, result.category);
            config.setAdditionalProductListQuantity(quantity);
            this.showAddedToast(groupName);
            this.productAdded$.next();
          } catch {
            this.toastHelper.error(
              $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
              $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
            );
          }
        },
        error: () => {
          this.toastHelper.error(
            $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
            $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
          );
        },
      });
  }

  private showAddedToast(name: string): void {
    this.toastHelper.success(
      $localize`:@@toast.productList.added.header:Zur Produktliste hinzugefügt`,
      $localize`:@@toast.productList.added.message:${name} wurde zur Produktliste hinzugefügt.`,
    );
  }

  private showQuantityIncrementModal(config: ProductConfiguration, name: string): void {
    const currentQuantity = config.additionalProductListQuantity;
    this.modalService
      .open(AddConfigToProductListModalComponent, {
        id: "add-config-to-product-list-modal",
        data: { title: name, rows: [{ quantity: 1, name, partNumber: config.mainProductId }], quantity: currentQuantity + 1 },
      })
      .onClose$.subscribe((quantity) => {
        if (typeof quantity !== "number") return;
        try {
          config.setAdditionalProductListQuantity(quantity);
          this.showAddedToast(name);
          this.productAdded$.next();
        } catch {
          this.toastHelper.error(
            $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
            $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
          );
        }
      });
  }

  private addDirectDeviceToProductList(result: MonitoringSearchResult & { kind: "device" }, project: MonitoringProject): void {
    const lines: MonitoringDirectBomLine[] = [MonitoringDirectBomLine.create(result.device.matNr, result.device.name, 1)];
    const existingEntry = project.findDirectBomEntryByLines(result.category, lines);

    if (existingEntry) {
      this.showDirectEntryQuantityIncrementModal(project, existingEntry, result.device.name);
      return;
    }

    this.openDirectEntryModal(result.device.name, lines, (quantity) => {
      const entry = MonitoringDirectBomEntry.create(result.device.name, result.category, quantity, lines);
      project.addDirectBomEntry(entry);
    });
  }

  private addDirectGroupToProductList(
    result: MonitoringSearchResult & { kind: "group" },
    project: MonitoringProject,
    deviceLookup: Map<string, Device>,
  ): void {
    const quantities = MonitoringProductItem.buildDeviceQuantityMap([MonitoringProductItem.create(result.option, 1)]);
    const lines: MonitoringDirectBomLine[] = Array.from(quantities.entries()).map(([partNumber, quantity]) =>
      MonitoringDirectBomLine.create(partNumber, deviceLookup.get(partNumber)?.name ?? partNumber, quantity),
    );
    const existingEntry = project.findDirectBomEntryByLines(result.category, lines);

    if (existingEntry) {
      this.showDirectEntryQuantityIncrementModal(project, existingEntry, result.option.groupName);
      return;
    }

    this.openDirectEntryModal(result.option.groupName, lines, (quantity) => {
      const entry = MonitoringDirectBomEntry.create(result.option.groupName, result.category, quantity, lines);
      project.addDirectBomEntry(entry);
    });
  }

  private openDirectEntryModal(name: string, lines: MonitoringDirectBomLine[], onConfirm: (quantity: number) => void) {
    const rows: AddConfigToProductListRow[] = lines.map((line) => ({
      quantity: line.quantity,
      name: line.name,
      partNumber: line.partNumber,
    }));

    this.modalService
      .open(AddConfigToProductListModalComponent, {
        id: "add-config-to-product-list-modal",
        data: { title: name, rows, quantity: 1 },
      })
      .onClose$.subscribe({
        next: (quantity) => {
          if (typeof quantity !== "number") return;
          try {
            onConfirm(quantity);
            this.showAddedToast(name);
            this.productAdded$.next();
          } catch {
            this.toastHelper.error(
              $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
              $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
            );
          }
        },
        error: () => {
          this.toastHelper.error(
            $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
            $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
          );
        },
      });
  }

  private showDirectEntryQuantityIncrementModal(project: MonitoringProject, entry: MonitoringDirectBomEntry, name: string): void {
    const rows: AddConfigToProductListRow[] = entry.lines.map((line) => ({
      quantity: line.quantity,
      name: line.name,
      partNumber: line.partNumber,
    }));

    this.modalService
      .open(AddConfigToProductListModalComponent, {
        id: "add-config-to-product-list-modal",
        data: { title: name, rows, quantity: entry.quantity + 1 },
      })
      .onClose$.subscribe((quantity) => {
        if (typeof quantity !== "number") return;
        try {
          project.setDirectBomEntryQuantity(entry, quantity);
          this.showAddedToast(name);
          this.productAdded$.next();
        } catch {
          this.toastHelper.error(
            $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
            $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
          );
        }
      });
  }
}
